<template>
  <!--
    THE MACHINES (Matt, 2026-10-15: "go through the loop, solve the
    deduction puzzle, and then that becomes the foundation for the next
    deduction puzzle"). One machine at a time: what goes in (first, then
    second), Fire, what came out, the law sheet the player strikes by hand,
    what the technologies of this tier need, and the shelf. The law itself
    is never rendered - like the eight's natures, it is the answer key.
  -->
  <div class="machines">
    <div class="mode-switch machines__pick" role="group" aria-label="Which machine">
      <button
        v-for="m in MACHINES"
        :key="m.id"
        :class="{ 'mode-switch--on': which === m.id }"
        @click="choose(m.id)"
      >
        {{ m.name }}
        <small>{{ standing(m.id) ? `tier ${m.tier}` : 'not built' }}</small>
      </button>
    </div>

    <!-- The notebook is one tap away from every machine (Andrew's closing
         ask, playtest 13: "the notebook needs to be accessible from every page"). -->
    <button class="linkish machines__book" @click="$emit('notebook')">Open the notebook ›</button>

    <!-- Not standing yet: say what it takes, and nothing else. -->
    <p v-if="!standing(machine.id)" class="phone__note machines__absent">
      <template v-if="!knowsHow(machine.id)">
        <b>{{ techName(machine.tech) }}</b> teaches the Stewards to build a {{ machine.name }}.
        <template v-if="machine.from"> It takes {{ below.products }} from the {{ below.name }}.</template>
      </template>
      <template v-else>The town knows how. Ask the Stewards to build a {{ machine.name }}.</template>
      Every tier {{ machine.tier }} technology is made from what it makes.
    </p>

    <template v-else>
      <!-- In goes the first, then the second: order can matter. -->
      <div class="machines__feed" data-tut="mc-feed">
        <button
          v-for="i in [0, 1]"
          :key="i"
          class="flask machines__slot"
          :class="{ 'flask--full': picked[i], 'flask--picking': picking === i }"
          :disabled="!canLook"
          @click="picking = picking === i ? null : i"
        >
          <span class="machines__ord">{{ i === 0 ? 'first in' : 'second in' }}</span>
          <span class="flask__name">
            <template v-if="!picked[i]">empty</template>
            <template v-else-if="!machine.from">{{ PRINCIPLE_NAMES[picked[i]] }} <span class="machines__known"><Aspect v-for="k in natureOf(picked[i])" :key="k.aspect" :aspect="k.aspect" :value="k.value" :word="false" /></span></template>
            <template v-else><Badges :k="picked[i]" /> {{ below.product }}</template>
          </span>
        </button>
      </div>

      <div v-if="picking !== null" class="picker machines__picker">
        <span class="picker__lead">{{ picking === 0 ? 'First in:' : 'Second in:' }}</span>
        <p v-if="!inputs.length" class="phone__note">Nothing on the shelf from the {{ below.name }} yet. Fire the {{ below.name }} first.</p>
        <ul class="picker__grid">
          <li v-for="x in inputs" :key="x">
            <button class="picker__one" @click="fill(x)">
              <template v-if="!machine.from">
                <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
                <svg class="picker__art" viewBox="0 0 24 24" aria-hidden="true" v-html="artForPrinciple(x)" />
                <span>{{ PRINCIPLE_NAMES[x] }}</span>
                <!-- Its nature, from the notebook, right here (Andrew, playtest
                     13: "I have to go back to my notebook and then back to
                     the kiln every time... it would be awesome if I could see
                     them side-by-side"). -->
                <span class="machines__known"><Aspect v-for="k in natureOf(x)" :key="k.aspect" :aspect="k.aspect" :value="k.value" :word="false" /></span>
              </template>
              <template v-else>
                <span><Badges :k="x" /></span>
                <span class="machines__count">×{{ shelfOfBelow[x] }}</span>
              </template>
            </button>
          </li>
        </ul>
      </div>

      <span v-if="picked[0] && picked[1]" class="machines__ready" aria-hidden="true" />
      <button
        class="action bench__go"
        data-tut="mc-fire"
        :class="{ 'action--off': !fireCheck.ok, 'action--preview': !canAct }"
        :disabled="!canAct || !fireCheck.ok"
        @click="fire"
      >
        <span class="action__top">
          <span class="action__label">Fire the {{ machine.name }} <ActionCost :cost="fireCost" /></span>
          <span class="action__gain">one {{ machine.product }} for the shelf</span>
        </span>
        <span v-if="!fireCheck.ok && picked.length === 2 && fireCheck.kind !== 'cost'" class="action__why">{{ fireCheck.why }}</span>
      </button>

      <!-- What came out of the last firing, until the next one. -->
      <div v-if="last" :key="lastKey" class="glass glass--result machines__out" data-tut="mc-out">
        <span class="glass__what">{{ inName(last.a) }}, then {{ inName(last.b) }}</span>
        <span class="glass__badges">
          <span class="glass__both">out comes</span>
          <Badges :k="last.out" :words="true" />
          <span class="glass__both">{{ machine.product }}</span>
        </span>
      </div>

      <!-- THE LAW SHEET: per property, every rule this machine might have.
           Struck by hand, kept on this phone; nothing here is worked out. -->
      <div class="laws" data-tut="mc-law">
        <h4 class="zone zone--flush">
          <span class="zone__label">The {{ machine.name }}'s law · strike what it can't be</span>
        </h4>
        <div v-for="a in ASPECTS" :key="a" class="laws__row">
          <span class="laws__aspect"><Aspect :aspect="a" :value="true" :word="false" /><Aspect :aspect="a" :value="false" :word="false" /></span>
          <span class="laws__options">
            <button
              v-for="law in lawsFor(rung, a)"
              :key="lawKey(law)"
              class="laws__one"
              :class="{ 'laws__one--out': struck(a, law), 'laws__one--in': ringed(a, law) }"
              :title="describeLaw(law)"
              @click="cycle(a, law)"
            >{{ shortLaw(law) }}</button>
          </span>
        </div>
        <p class="laws__how">Tap to strike, again to ring, again to clear. "1st" is the first one in.</p>
      </div>

      <!-- What the technologies of this tier want, and Make when the shelf has it. -->
      <div v-if="wanted.length" class="aims machines__wanted" data-tut="mc-wanted">
        <span class="aims__label">Technologies made from {{ machine.products }}</span>
        <div class="aims__row">
          <button
            v-for="t in wanted"
            :key="t.id"
            class="aim"
            :class="{ 'aim--on': t.ok }"
            :disabled="!canAct || !t.ok"
            @click="make(t.id)"
          >
            <span class="aim__name">{{ t.ok ? 'Make ' : '' }}{{ t.name }} <Pip kind="knowledge" :n="t.price" /></span>
            <span class="aim__need">
              a {{ machine.product }} that is
              <Aspect v-for="k in t.need" :key="k.aspect" :aspect="k.aspect" :value="k.value" :word="false" />
            </span>
          </button>
        </div>
      </div>

      <!-- The shelf, this machine's row. -->
      <p class="machines__shelf">
        <span class="aims__label">On the shelf</span>
        <template v-if="shelfRows.length">
          <span v-for="r in shelfRows" :key="r.k" class="machines__item"><Badges :k="r.k" /> ×{{ r.n }}</span>
        </template>
        <span v-else class="muted">no {{ machine.products }} yet</span>
      </p>

      <!-- Every firing, newest first. -->
      <details v-if="firings.length" class="phone__log machines__record">
        <summary>Every firing · {{ firings.length }}</summary>
        <ul class="record record--open">
          <li v-for="(f, i) in [...firings].reverse()" :key="i">
            <span class="record__pair">{{ inName(f.a) }} → {{ inName(f.b) }}</span>
            <span class="record__said"><Badges :k="f.out" /></span>
          </li>
        </ul>
      </details>
    </template>

    <details class="rules">
      <summary>How a machine works</summary>
      <ol class="rules__list">
        <li><b>Feed it two things, one after the other.</b> One new thing comes out, and you see whether it is hot or cold, wet or dry, heavy or light.</li>
        <li><b>The machine has a hidden law: one rule for each property.</b> Each rule is one of the ones on the sheet, like "hot if either one in is hot" or "like the first one in".</li>
        <li><b>Work the law out.</b> Compare what came out with what went in, property by property, and strike the rules that could not have given it. Feeding it two that differ in only one property tells you the most.</li>
        <li><b>The Kiln takes two of the eight</b>, so what you know about the eight matters here. The Still takes two glazes; the Crucible two essences. What goes in is used up.</li>
        <li><b>A technology names what it needs.</b> When the shelf holds a fit, Make it: the thing is used, and the knowledge pays for writing it down.</li>
      </ol>
    </details>
  </div>
</template>

<script>
import { h } from 'vue';
import {
  actionsForRole, checkAction, resolveCost, techPrice, availableTechs,
} from '@/engine/roleActions';
import { TECHS } from '@/engine/cards';
import { canBuildType, machineStands } from '@/engine/city';
import {
  ASPECTS, PRINCIPLE_NAMES, aspectWord, knownOf,
} from '@/engine/lore';
import {
  MACHINES, machineById, lawsFor, lawKey, describeLaw, shortLaw, rungOf, inputsOf, firingsOf, shelfOf, natureFromKey,
  productFits, machineForTier,
} from '@/engine/machines';
import { artForPrinciple } from './loreArt';
import ActionCost from './ActionCost.vue';
import Pip from './Pip.vue';
import Aspect from './Aspect.vue';

// A product drawn as its three badges (and words, when there is room).
const Badges = {
  name: 'Badges',
  props: { k: { type: String, required: true }, words: { type: Boolean, default: false } },
  setup(props) {
    return () => {
      const n = natureFromKey(props.k);
      if (!n) return h('span', props.k);
      return h('span', { class: 'machines__badges', title: ASPECTS.map((a) => aspectWord(a, n[a])).join(', ') },
        ASPECTS.map((a) => h(Aspect, { aspect: a, value: n[a], word: props.words, key: a })));
    };
  },
};

export default {
  name: 'MachinesPanel',
  components: { ActionCost, Pip, Aspect, Badges },
  props: {
    game: { type: Object, required: true },
    civ: { type: Object, required: true },
    canAct: { type: Boolean, default: false },
    canLook: { type: Boolean, default: false },
    // A technology to open on (from the tree).
    aim: { type: String, default: '' },
  },
  emits: ['act', 'notebook'],
  data() {
    const first = MACHINES.find((m) => machineStands(this.civ, m.id)) || MACHINES[0];
    const fromAim = this.aim && TECHS[this.aim] ? machineForTier(TECHS[this.aim].tier) : null;
    return {
      MACHINES,
      ASPECTS,
      PRINCIPLE_NAMES,
      which: (fromAim || first).id,
      picked: [],
      picking: null,
      marks: {},
      seenFirings: firingsOf(this.civ, (fromAim || first).id).length,
      lastKey: 0,
    };
  },
  computed: {
    machine() { return machineById(this.which); },
    below() { return this.machine.from ? machineById(this.machine.from) : null; },
    rung() { return rungOf(this.which); },
    inputs() { return inputsOf(this.civ, this.which); },
    shelfOfBelow() { return this.machine.from ? shelfOf(this.civ, this.machine.from) : {}; },
    firings() { return firingsOf(this.civ, this.which); },
    last() { return this.firings.length > this.seenFirings ? this.firings[this.firings.length - 1] : null; },
    params() { return { machine: this.which, a: this.picked[0], b: this.picked[1] }; },
    fireCheck() { return checkAction(this.game, 'science', 'fire', this.params); },
    fireCost() { return resolveCost(actionsForRole('science').find((a) => a.id === 'fire'), this.civ, 1, this.params, this.game); },
    wanted() {
      return availableTechs(this.civ)
        .filter((id) => TECHS[id].tier === this.machine.tier)
        .map((id) => {
          const need = this.civ.lore?.reactions?.[id] || {};
          const ok = Object.keys(shelfOf(this.civ, this.which)).some((k) => shelfOf(this.civ, this.which)[k] > 0 && productFits(k, need))
            && checkAction(this.game, 'science', 'make', { target: id }).ok;
          return {
            id,
            name: TECHS[id].name,
            price: techPrice(this.civ, id),
            need: ASPECTS.filter((k) => k in need).map((k) => ({ aspect: k, value: need[k] })),
            ok,
          };
        });
    },
    shelfRows() {
      return Object.entries(shelfOf(this.civ, this.which)).filter(([, n]) => n > 0).map(([k, n]) => ({ k, n }));
    },
    markKey() { return `commonwealth:laws:${this.game.seed ?? 'x'}:${this.which}`; },
  },
  watch: {
    which() { this.loadMarks(); },
    'firings.length'(now, was) {
      // A new firing: the glass shows it (and empties the slots for the next).
      if (now > was) { this.lastKey += 1; this.picked = []; this.picking = null; }
    },
  },
  mounted() { this.loadMarks(); },
  methods: {
    /** What the notebook knows of a principle, in ink: [{ aspect, value }]. */
    natureOf(x) {
      const k = knownOf(this.civ, x);
      return ASPECTS.filter((a) => k[a] !== undefined).map((a) => ({ aspect: a, value: k[a] }));
    },
    techName(id) { return TECHS[id]?.name || id; },
    standing(id) { return machineStands(this.civ, id); },
    knowsHow(id) { return canBuildType(this.civ, id) || machineStands(this.civ, id); },
    inName(x) {
      if (!this.machine.from) return PRINCIPLE_NAMES[x] || x;
      const n = natureFromKey(x);
      return n ? `${ASPECTS.map((a) => aspectWord(a, n[a])).join(' ')} ${this.below.product}` : x;
    },
    choose(id) {
      this.which = id;
      this.picked = [];
      this.picking = null;
      this.seenFirings = firingsOf(this.civ, id).length;
    },
    fill(x) {
      const i = this.picking ?? (this.picked[0] ? 1 : 0);
      const next = [...this.picked];
      next[i] = x;
      this.picked = next;
      this.picking = next[0] && next[1] ? null : (next[0] ? 1 : 0);
    },
    fire() {
      if (!this.fireCheck.ok) return;
      this.seenFirings = this.firings.length;
      this.$emit('act', { role: 'science', actionId: 'fire', params: { ...this.params } });
    },
    make(id) { this.$emit('act', { role: 'science', actionId: 'make', params: { target: id } }); },
    // ---- the law sheet, struck by hand
    lawsFor,
    lawKey,
    describeLaw,
    shortLaw,
    artForPrinciple,
    markOf(a, law) { return this.marks[`${a}.${lawKey(law)}`] || null; },
    struck(a, law) { return this.markOf(a, law) === 'out'; },
    ringed(a, law) { return this.markOf(a, law) === 'in'; },
    cycle(a, law) {
      const k = `${a}.${lawKey(law)}`;
      const now = this.marks[k];
      const next = now === 'out' ? 'in' : (now === 'in' ? null : 'out');
      const marks = { ...this.marks };
      if (next) marks[k] = next; else delete marks[k];
      this.marks = marks;
      try { localStorage.setItem(this.markKey, JSON.stringify(marks)); } catch { /* storage off */ }
    },
    loadMarks() {
      try { this.marks = JSON.parse(localStorage.getItem(this.markKey) || '{}') || {}; } catch { this.marks = {}; }
    },
  },
};
</script>

<style lang="scss" scoped>
.machines { display: flex; flex-direction: column; gap: 10px; }
.machines__pick { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; }
.machines__pick button { display: flex; flex-direction: column; align-items: center; line-height: 1.1; padding: 4px 2px; min-width: 0; }
.machines__pick small { font-size: 0.7rem; opacity: 0.75; }
.machines__absent { margin: 0; }
.machines__feed { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.machines__slot { display: flex; flex-direction: column; align-items: center; gap: 0; min-height: 44px; padding: 4px; }
.machines__ord { font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.06em; opacity: 0.7; }
.machines__count { font-size: 0.75rem; opacity: 0.8; }
.machines__ready { display: none; }
.machines__out { animation: machines-out 0.9s ease-out; }
@keyframes machines-out {
  0% { transform: scale(0.92); box-shadow: 0 0 0 0 rgba(217, 164, 65, 0.9); }
  60% { transform: scale(1.03); box-shadow: 0 0 18px 6px rgba(217, 164, 65, 0.5); }
  100% { transform: scale(1); box-shadow: none; }
}
@media (prefers-reduced-motion: reduce) { .machines__out { animation: none; } }
.machines__shelf { display: flex; flex-wrap: wrap; align-items: center; gap: 6px 10px; margin: 0; }
.machines__item { display: inline-flex; align-items: center; gap: 4px; }
:deep(.machines__badges) { display: inline-flex; gap: 2px; vertical-align: middle; }
.laws { display: flex; flex-direction: column; gap: 6px; }
.laws__row { display: grid; grid-template-columns: 36px 1fr; gap: 4px; align-items: center; }
.laws__aspect { display: inline-flex; gap: 2px; padding-top: 4px; }
.laws__options { display: flex; flex-wrap: wrap; gap: 4px; }
.laws__one {
  font-size: 0.72rem; padding: 2px 6px; border-radius: 9px; min-height: 0; line-height: 1.3;
  border: 1px solid var(--line, rgba(255, 255, 255, 0.25)); background: transparent; color: inherit;
}
.laws__one--out { text-decoration: line-through; opacity: 0.45; }
.laws__one--in { border-color: #7aa7ff; box-shadow: inset 0 0 0 1px #7aa7ff; }
.laws__how { font-size: 0.72rem; opacity: 0.7; margin: 0; }
.muted { opacity: 0.6; font-size: 0.8rem; }
</style>
